// =============================================================================
// FILE: packages/renderer/src/components/public/RetailMenu.tsx
// PURPOSE: "Retail À-La-Carte Menu" — a self-service storefront on the landing
//   page. Independent customers preview an On-Demand tool before package pricing,
//   capture a package lead into the waitlist funnel, and see the Legacy
//   License + "Unlock the Whole Engine" subscription CTA.
// =============================================================================

import { useState, type JSX } from 'react';
import { X, ShoppingCart, Infinity as InfinityIcon, ShieldCheck } from 'lucide-react';
import { PERPETUAL_LICENSE } from '../../config/pricing.js';
import { ManualPaymentNotice } from '../purchase/ManualPaymentNotice.js';
import { PurchaseRequestModal, type PurchaseTarget } from '../purchase/PurchaseRequestModal.js';

interface Product { id: string; name: string; price: string; desc: string; color: string; }

const PRODUCTS: Product[] = [
  { id: 'rack-drafter',      name: 'On-Demand Rack Drafter',       price: 'Free', desc: 'Professional 3D rack elevations, certified PDF.',            color: '#FCD34D' },
  { id: 'permit-engine',     name: 'URD Permit Engine',            price: 'Free', desc: 'Utility / public-road permit drawing sets.',                color: '#F97066' },
  { id: 'compliance-auditor', name: 'Compliance Pass & Cert',      price: 'Free', desc: 'TIA / NEC / CEC / EU audit + Certificate of Compliance.',   color: '#34D399' },
  { id: 'spatial-estimator', name: 'Spatial Estimator',            price: 'Free', desc: 'Instant coordinate-to-cost cabling bid.',                   color: '#2DD4BF' },
  { id: 'wireless-architect', name: 'Wireless Architect',          price: 'Free', desc: 'Signal heat maps & AP placement.',                          color: '#38BDF8' },
  { id: 'av-master',         name: 'AV Master — Conf Rooms',       price: 'Free', desc: 'Certified conference-room AV packages.',                     color: '#A78BFA' },
];

export function RetailMenu({ onClose, onOpenTool }: { onClose: () => void; onOpenTool?: (id: string) => void }): JSX.Element {
  const [purchase, setPurchase] = useState<PurchaseTarget | null>(null);

  return (
    <div data-testid="retail-menu" className="fixed inset-0 z-[10000] grid place-items-center p-6" style={{ background: 'rgba(4,7,14,0.9)', backdropFilter: 'blur(8px)' }}>
      <div className="relative w-full max-w-4xl rounded-2xl overflow-hidden" style={{ background: 'rgba(9,13,22,0.98)', border: '1px solid rgba(37,181,168,0.35)', boxShadow: '0 30px 120px rgba(0,0,0,0.75)', maxHeight: '92vh' }}>
        <div className="flex items-center justify-between px-6 py-4" style={{ borderBottom: '1px solid rgba(37,181,168,0.2)' }}>
          <div className="flex items-center gap-3">
            <ShoppingCart className="h-5 w-5 text-teal-300" />
            <div>
              <div className="text-[10px] uppercase tracking-[0.24em] text-teal-300/80">RCD² Storefront</div>
              <div className="text-sm font-bold text-zinc-100">À-La-Carte Engineering Services · Free Preview</div>
            </div>
          </div>
          <button data-testid="retail-close" onClick={onClose} className="h-8 w-8 grid place-items-center rounded-md text-zinc-500 hover:text-zinc-200" style={{ border: '1px solid rgba(63,63,70,0.6)' }}><X className="h-4 w-4" /></button>
        </div>

        <div className="overflow-y-auto p-6" style={{ maxHeight: 'calc(92vh - 66px)' }}>
          <div className="mb-5"><ManualPaymentNotice /></div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {PRODUCTS.map((p) => (
              <div key={p.id} data-testid={`retail-${p.id}`} className="rounded-xl p-4 flex flex-col" style={{ background: 'rgba(14,20,38,0.7)', border: `1px solid ${p.color}44` }}>
                <div data-testid={`retail-free-tag-${p.id}`} className="inline-flex w-fit items-center rounded-full px-2 py-1 text-[10px] uppercase tracking-[0.18em] font-extrabold" style={{ color: '#06111f', background: p.color }}>Free</div>
                <div className="text-[13px] font-bold text-zinc-100 mt-1">{p.name}</div>
                <div className="text-[11px] text-zinc-400 mt-1 flex-1">{p.desc}</div>
                <div className="flex gap-2 mt-3">
                  {onOpenTool && ['rack-drafter', 'spatial-estimator'].includes(p.id) && (
                    <button data-testid={`retail-preview-${p.id}`} onClick={() => onOpenTool(p.id)} className="px-3 py-1.5 rounded-sm text-[11px] text-zinc-300 border border-zinc-700 hover:border-zinc-500">Preview</button>
                  )}
                  <button data-testid={`retail-buy-${p.id}`} onClick={() => setPurchase({ id: p.id, name: p.name, category: 'À-La-Carte', price: p.price })}
                    className="flex-1 px-3 py-1.5 rounded-sm text-[11px] font-bold transition"
                    style={{ background: p.color, color: '#0a0f1a' }}>
                    View Package
                  </button>
                </div>
              </div>
            ))}
          </div>

          {/* $5,999 Legacy perpetual license */}
          <div data-testid="legacy-license-card" className="mt-6 rounded-xl p-5" style={{ background: 'linear-gradient(135deg, rgba(252,211,77,0.1), rgba(37,181,168,0.06))', border: '1px solid rgba(252,211,77,0.4)' }}>
            <div className="flex items-start justify-between gap-4 flex-wrap">
              <div className="flex-1 min-w-[240px]">
                <div className="flex items-center gap-2">
                  <InfinityIcon className="h-5 w-5 text-amber-300" />
                  <span className="text-[15px] font-extrabold text-amber-200">{PERPETUAL_LICENSE.name}</span>
                </div>
                <div className="text-[12px] text-zinc-300 mt-1">{PERPETUAL_LICENSE.tagline}</div>
                <div className="flex items-center gap-2 mt-2 text-[12px] text-emerald-300"><ShieldCheck className="h-4 w-4" /> {PERPETUAL_LICENSE.guarantee}</div>
                <div className="text-[11px] text-zinc-500 mt-1">{PERPETUAL_LICENSE.note}</div>
              </div>
              <div className="text-right">
                <div className="text-3xl font-extrabold text-zinc-50">${PERPETUAL_LICENSE.priceUSD.toLocaleString()}</div>
                <div className="text-[10px] uppercase tracking-widest text-zinc-500">USD · perpetual</div>
                <button data-testid="legacy-buy-btn" onClick={() => setPurchase({ id: 'legacy', name: PERPETUAL_LICENSE.name, category: 'Legacy License', price: `$${PERPETUAL_LICENSE.priceUSD}` })}
                  className="mt-2 px-4 py-2 rounded-lg text-[12px] font-bold bg-amber-300 text-zinc-900 hover:bg-amber-200">
                  Own it Forever
                </button>
              </div>
            </div>
          </div>

          <div className="mt-4 text-center">
            <p className="text-[12px] text-zinc-400">Want everything, always up to date? <span className="text-teal-300 font-semibold">Unlock the Whole Engine</span> with a monthly subscription — start a 100-day trial.</p>
          </div>
        </div>
      </div>
      {purchase && <PurchaseRequestModal product={purchase} onClose={() => setPurchase(null)} />}
    </div>
  );
}
